import React, { useEffect, useRef, useState } from "react";
import { Eraser } from "lucide-react";
import { Button } from "@/components/ui/button";

/** Lightweight signature pad — no external dependency. Draws on a raw HTML5
 * canvas via pointer events and exposes the result as a PNG data URL. */
export function SignatureCanvas({ onChange, testId = "signature-canvas" }) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastRef = useRef({ x: 0, y: 0 });
  const [hasStroke, setHasStroke] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.4;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0C1A1A";
  }, []);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    return { x: p.clientX - rect.left, y: p.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    lastRef.current = pos(e);
  };

  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastRef.current = p;
    setHasStroke(true);
  };

  const end = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    onChange?.(canvasRef.current.toDataURL("image/png"));
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasStroke(false);
    onChange?.("");
  };

  return (
    <div className="space-y-2">
      <div className="rounded-xl border-2 border-dashed border-[#D1D5DB] bg-[#FAFAF9] relative">
        <canvas
          ref={canvasRef}
          className="w-full h-40 rounded-xl cursor-crosshair touch-none"
          data-testid={testId}
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        />
        {!hasStroke && (
          <div className="absolute inset-0 grid place-items-center pointer-events-none">
            <span className="text-sm text-[#9CA3AF]">Sign here with your mouse or finger</span>
          </div>
        )}
      </div>
      <Button type="button" variant="outline" size="sm" onClick={clear} className="rounded-lg text-xs" data-testid={`${testId}-clear-btn`}>
        <Eraser className="w-3.5 h-3.5 mr-1.5" /> Clear
      </Button>
    </div>
  );
}
